<script setup>
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  clients: { type: Array,  default: () => [] },
  new_url: { type: String, default: '#' },
})

const openDropdown = ref(null)
function toggleDropdown(id) {
  openDropdown.value = openDropdown.value === id ? null : id
}

const search      = ref('')
const pageSize    = ref(25)
const currentPage = ref(1)
const sortKey     = ref('nom')
const sortDir     = ref('asc')

watch([search, pageSize, sortKey, sortDir], () => { currentPage.value = 1 })

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'asc'
  }
}

const filtered = computed(() => {
  let list = props.clients
  if (search.value.trim()) {
    const q = search.value.toLowerCase()
    list = list.filter(c =>
      (c.nom       || '').toLowerCase().includes(q) ||
      (c.subdomain || '').toLowerCase().includes(q) ||
      (c.schema    || '').toLowerCase().includes(q) ||
      (c.address   || '').toLowerCase().includes(q)
    )
  }
  return [...list].sort((a, b) => {
    let aVal = a[sortKey.value] ?? ''
    let bVal = b[sortKey.value] ?? ''
    if (typeof aVal === 'string') aVal = aVal.toLowerCase()
    if (typeof bVal === 'string') bVal = bVal.toLowerCase()
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ?  1 : -1
    return 0
  })
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / pageSize.value)))
const paginated  = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filtered.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const pages = []
  for (let i = 1; i <= totalPages.value; i++) pages.push(i)
  return pages
})

function destroyClient(client) {
  if (!confirm(`Supprimer le client « ${client.nom} » ? Cette action est irréversible.`)) return
  router.delete(client.delete_url, { preserveScroll: true })
}
</script>

<template>
  <AppLayout>
    <!-- TopBar left: search -->
    <template #topbar-left>
      <div class="relative w-80 group">
        <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-sm pointer-events-none">search</span>
        <input
          v-model="search"
          type="text"
          placeholder="Rechercher un client..."
          class="w-full bg-surface-container-highest border-none rounded-md pl-10 pr-4 py-2 text-sm text-on-surface placeholder-outline focus:ring-2 focus:ring-primary/20 outline-none transition-all"
        />
      </div>
    </template>

    <div class="p-8" @click="openDropdown = null">

      <!-- Page header -->
      <div class="flex justify-between items-end mb-6">
        <div>
          <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase mb-2">Gestion des Clients</h1>
          <div class="flex items-center gap-4 text-[10px] font-black text-outline uppercase tracking-widest">
            <span class="flex items-center gap-1.5">
              <span class="w-2 h-2 rounded-full bg-primary"></span>
              {{ clients.length }} Client{{ clients.length > 1 ? 's' : '' }}
            </span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <!-- Sélecteur par page -->
          <div class="flex items-center gap-2 bg-surface-container-low px-3 py-2 rounded-md border border-outline-variant/10">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">Affichage</span>
            <select
              v-model="pageSize"
              class="bg-transparent border-none text-xs font-black text-primary p-0 focus:ring-0 cursor-pointer uppercase outline-none"
            >
              <option :value="10">10 Clients</option>
              <option :value="25">25 Clients</option>
              <option :value="50">50 Clients</option>
            </select>
          </div>
          <!-- Bouton Nouveau -->
          <a
            :href="new_url"
            class="machined-sheen px-5 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg"
          >
            <span class="material-symbols-outlined text-sm">add_business</span>
            Nouveau Client
          </a>
        </div>
      </div>

      <!-- Table -->
      <div class="bg-surface-container rounded-xl overflow-hidden shadow-2xl border border-outline-variant/10">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-surface-container-high/60 border-b border-outline-variant/15">
              <th @click="toggleSort('nom')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Nom<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'nom' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th @click="toggleSort('subdomain')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-40 cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Sous-domaine<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'subdomain' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th @click="toggleSort('schema')" class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline w-40 cursor-pointer select-none hover:text-on-surface transition-colors">
                <span class="flex items-center gap-1">Schéma BD<span class="material-symbols-outlined text-[12px]">{{ sortKey === 'schema' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</span></span>
              </th>
              <th class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline">Adresse</th>
              <th class="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-outline text-right w-24">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-outline-variant/10">
            <tr
              v-for="(c, i) in paginated"
              :key="c.id"
              :class="[
                'group hover:bg-surface-container-highest/20 transition-colors',
                i % 2 === 1 ? 'bg-surface-container-low/20' : '',
              ]"
            >
              <!-- Nom -->
              <td class="px-6 py-4 align-top">
                <a :href="c.show_url" class="font-bold text-primary hover:brightness-125 text-sm uppercase tracking-tight">
                  {{ c.nom || '—' }}
                </a>
              </td>
              <!-- Sous-domaine -->
              <td class="px-6 py-4 align-top">
                <code class="text-xs font-mono font-black text-on-surface">{{ c.subdomain || '—' }}</code>
              </td>
              <!-- Schéma BD -->
              <td class="px-6 py-4 align-top">
                <span class="inline-flex items-center px-2.5 py-1 rounded bg-primary/10 border border-primary/20 text-[10px] font-black uppercase tracking-widest text-primary">
                  {{ c.schema || '—' }}
                </span>
              </td>
              <!-- Adresse -->
              <td class="px-6 py-4 align-top text-xs text-outline">
                {{ c.address || '—' }}
              </td>
              <!-- Actions -->
              <td class="px-6 py-4 align-top text-right">
                <div class="relative inline-block">
                  <button
                    @click.stop="toggleDropdown(c.id)"
                    class="inline-flex p-2 text-outline hover:text-primary hover:bg-primary/10 rounded transition-all"
                  >
                    <span class="material-symbols-outlined text-lg">edit_note</span>
                  </button>
                  <div
                    v-if="openDropdown === c.id"
                    class="absolute right-0 top-full z-50 mt-1 bg-surface-container-high border border-outline-variant/20 rounded-lg shadow-xl min-w-[160px] py-1"
                  >
                    <a
                      :href="c.show_url"
                      class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors"
                    >
                      <span class="material-symbols-outlined text-base">visibility</span>
                      Voir
                    </a>
                    <a
                      :href="c.edit_url"
                      class="flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-on-surface hover:bg-surface-container-highest transition-colors"
                    >
                      <span class="material-symbols-outlined text-base">edit_note</span>
                      Modifier
                    </a>
                    <button
                      type="button"
                      @click.stop="destroyClient(c); openDropdown = null"
                      class="w-full flex items-center gap-2 px-4 py-2 text-sm text-outline hover:text-error hover:bg-error/10 transition-colors"
                    >
                      <span class="material-symbols-outlined text-base">delete</span>
                      Supprimer
                    </button>
                  </div>
                </div>
              </td>
            </tr>
            <!-- Empty state -->
            <tr v-if="paginated.length === 0">
              <td colspan="5" class="px-6 py-16 text-center text-outline text-sm">
                <span class="material-symbols-outlined text-4xl block mb-3 opacity-30">domain_disabled</span>
                Aucun client trouvé{{ search ? ` pour "${search}"` : '' }}.
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination footer -->
        <div class="px-6 py-4 bg-surface-container-high/40 border-t border-outline-variant/15 flex justify-between items-center">
          <div class="text-[10px] text-outline font-black uppercase tracking-widest">
            Affichage de {{ filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}
            à {{ Math.min(currentPage * pageSize, filtered.length) }}
            sur {{ filtered.length }} client{{ filtered.length > 1 ? 's' : '' }}
          </div>
          <div class="flex gap-1">
            <button
              :disabled="currentPage === 1"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage--"
            >
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <template v-for="p in pageNumbers" :key="p">
              <button
                v-if="p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1"
                :class="[
                  'w-8 h-8 flex items-center justify-center rounded text-[10px] font-black transition-colors',
                  p === currentPage
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface-container-low border border-outline-variant/10 text-outline hover:text-primary'
                ]"
                @click="currentPage = p"
              >{{ p }}</button>
              <span
                v-else-if="p === 2 || p === totalPages - 1"
                class="w-8 h-8 flex items-center justify-center text-outline text-sm"
              >…</span>
            </template>
            <button
              :disabled="currentPage === totalPages"
              class="w-8 h-8 flex items-center justify-center rounded bg-surface-container border border-outline-variant/15 text-outline hover:text-primary disabled:opacity-30 transition-colors"
              @click="currentPage++"
            >
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
        Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
      </footer>

    </div>
  </AppLayout>
</template>
